import {
  SEConsentPlugin,
  SEConsentPurpose,
  SEConsentStatus,
} from '@schmap/cordova-plugin-se-consent/www/SEConsentPlugin';
import * as Sentry from '@sentry/react';
import { Auth } from 'aws-amplify';

import { SCHNEIDER_CHARGE_ENV_TYPE_STORAGE_KEY } from '../../../env-type';
import { i18n } from '../../../i18n';
import { trackDynatraceConsentArrayResponseReceived } from '../../../utils/native/dynatrace-events';
import { CIAM_IDMS_BASE_URL, getFreshEimTokens } from '../../auth/auth-helpers';

const viteEnv = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
const ENV_TYPE_SHORTHAND = localStorage.getItem(SCHNEIDER_CHARGE_ENV_TYPE_STORAGE_KEY) ?? 'STAGING';

export const PREFERENCE_CENTER_ID =
  ENV_TYPE_SHORTHAND === 'STAGING' ? 'fb0c62da-83e5-449c-b6d5-29aead868bf5' : 'f30e5c97-d4c1-4bfb-b91b-bb1438a12f06';

export const COLLECTION_POINT_ID =
  ENV_TYPE_SHORTHAND === 'STAGING' ? '000dceef-c0a1-4be6-83ee-80449c963751' : 'adc93251-a6c7-460a-96d4-82ab6aa17e7b';

// OneTrust purpose ID for the analytics-consent purpose associated with this collection point.
export const ANALYTICS_CONSENT_PURPOSE_ID =
  ENV_TYPE_SHORTHAND === 'STAGING' ? '689ccdee-51b2-4171-8ee2-d6443209a38f' : 'f3a8dc5c-aa01-4ece-9588-4b5fbef93b6d';

// Native SEConsentPlugin calls can hang indefinitely if the callback is never invoked (e.g. bridge not ready).
const CONSENT_PLUGIN_TIMEOUT_MS = 15_000;

const SE_APIGEE_KEY = viteEnv?.[`VITE_${ENV_TYPE_SHORTHAND}_SE_APIGEE_KEY`] as string; // APIGEE Client ID
const SE_APIGEE_SECRET = viteEnv?.[`VITE_${ENV_TYPE_SHORTHAND}_SE_APIGEE_SECRET`] as string;

const CONFIGURATIONS = {
  config: {
    apigeeConfig: {
      apiKey: SE_APIGEE_KEY,
      apiSecret: SE_APIGEE_SECRET,
      baseApiUrl: CIAM_IDMS_BASE_URL,
    },
    preferenceCenterId: PREFERENCE_CENTER_ID,
    collectionPointId: COLLECTION_POINT_ID,
  },
};

let consentManagementSetupPromise: Promise<void> | null = null;
let consentScreenPromise: Promise<SEConsentPurpose[] | SEConsentStatus> | null = null;
let consentSettingsPromise: Promise<SEConsentPurpose[] | SEConsentStatus> | null = null;
let consentRefreshPromise: Promise<SEConsentStatus> | null = null;

// SE Consent plugin setup, see docs at: https://didactic-fiesta-3d48cfd4.pages.github.io/ --
// -- You need to be part of the SCHMAP Github organisation to access these docs.
// Ensures the plugin is configured, shows the consent screen the first time it's needed, and
// returns the resulting purposes so callers can check selection state without a second setup call.
export async function instantiateConsentManagement(): Promise<SEConsentPurpose[]> {
  try {
    await ensureConsentManagementConfigured();

    const consentStatus = await refreshConsentOnce();
    if (consentStatus.isNeedToShowConsent) {
      await showConsentScreenOnce();
      return (await refreshConsentOnce()).purposes;
    }
    return consentStatus.purposes;
  } catch (e) {
    console.error(e);
    Sentry.captureException(e, {
      tags: {
        category: 'feature',
        feature: 'consent-management',
        helper: 'consent-management',
        method: 'instantiateConsentManagement',
      },
    });
    return [];
  }
}

export async function configureConsentManagementSettings(): Promise<SEConsentPurpose[]> {
  try {
    await ensureConsentManagementConfigured();
    await refreshConsentOnce();
    await showConsentFromSettingsOnce();
    return (await refreshConsentOnce()).purposes;
  } catch (error) {
    console.error(error);
    throw error;
  }
}

function ensureConsentManagementConfigured(): Promise<void> {
  consentManagementSetupPromise ??= withTimeout(configureConsentManagement(), 'consent management setup').catch(
    (error) => {
      consentManagementSetupPromise = null;
      throw error;
    }
  );

  return consentManagementSetupPromise;
}

function withTimeout<T>(promise: Promise<T>, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(
      () => reject(new Error(`Timed out waiting for ${label} after ${CONSENT_PLUGIN_TIMEOUT_MS}ms`)),
      CONSENT_PLUGIN_TIMEOUT_MS
    );
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      }
    );
  });
}

async function configureConsentManagement() {
  await initializeWithConfig();
  await activate();
}

// The native SDK keeps the last user/token/language it was given, so rebind them before each refresh
// to follow logins, token expiry and in-app language changes.
async function syncConsentUser() {
  const [{ ciamIdpAccessToken }, currentUser] = await Promise.all([
    getFreshEimTokens(),
    Auth.currentAuthenticatedUser(),
  ]);
  await setUserId(currentUser.attributes['email']);
  await setAccessToken(ciamIdpAccessToken);
  // The SDK expects an ISO-639 code (e.g. 'en'), not the app's full locale (e.g. 'en-US').
  await setLanguage(i18n.language.split('-')[0]);
}

// Call on logout so the native SDK no longer holds the previous user's identity or cIAM token.
export async function resetConsentManagement(): Promise<void> {
  const wasConfigured = consentManagementSetupPromise !== null;
  consentRefreshPromise = null;
  consentScreenPromise = null;
  consentSettingsPromise = null;
  if (!wasConfigured) return;

  await Promise.allSettled([
    withTimeout(setAccessToken(null), 'consent access token reset'),
    withTimeout(setUserId(null), 'consent user reset'),
  ]);
}

function initializeWithConfig() {
  return new Promise<void>((resolve, reject) => {
    SEConsentPlugin.initializeWithConfig(
      CONFIGURATIONS,
      'config', // Active config name. Must be a key from "configurations" object.
      () => {
        resolve();
      },
      (error) => reject('initializeWithConfig error: ' + error)
    );
  });
}

function activate() {
  return new Promise<void>((resolve, reject) => {
    SEConsentPlugin.activate(
      () => {
        resolve();
      },
      (error) => reject('activate error: ' + error)
    );
  });
}

function setUserId(email: string | null) {
  return new Promise<void>((resolve, reject) => {
    SEConsentPlugin.setUserId(
      email,
      () => {
        resolve();
      },
      (error) => reject('setUserId error: ' + error)
    );
  });
}

function setAccessToken(accessToken: string | null) {
  return new Promise<void>((resolve, reject) => {
    SEConsentPlugin.setAccessToken(
      accessToken,
      () => {
        resolve();
      },
      (error) => reject('setAccessToken error: ' + error)
    );
  });
}

function setLanguage(language: string) {
  return new Promise<void>((resolve, reject) => {
    SEConsentPlugin.setLanguage(
      language,
      () => {
        resolve();
      },
      (error) => reject('setLanguage error: ' + error)
    );
  });
}

function refreshConsent() {
  return new Promise<SEConsentStatus>((resolve, reject) => {
    SEConsentPlugin.refreshConsent(
      (consentStatus) => {
        if (Array.isArray(consentStatus)) {
          trackDynatraceConsentArrayResponseReceived('refreshConsent', consentStatus);
          reject('refreshConsent returned an unexpected purpose array');
          return;
        }
        resolve(consentStatus);
      },
      (error) => reject('refreshConsent error: ' + error)
    );
  });
}

function refreshConsentOnce(): Promise<SEConsentStatus> {
  consentRefreshPromise ??= syncConsentUser()
    .then(refreshConsent)
    .finally(() => {
      consentRefreshPromise = null;
    });

  return consentRefreshPromise;
}

function showConsentScreen() {
  return new Promise<SEConsentPurpose[] | SEConsentStatus>((resolve, reject) => {
    SEConsentPlugin.showConsentScreen(
      (purposes) => {
        if (Array.isArray(purposes)) trackDynatraceConsentArrayResponseReceived('showConsentScreen', purposes);
        resolve(purposes);
      },
      (error) => reject('showConsentScreen error: ' + error)
    );
  });
}

function showConsentFromSettings() {
  return new Promise<SEConsentPurpose[] | SEConsentStatus>((resolve, reject) => {
    SEConsentPlugin.showConsentFromSettings(
      (purposes) => {
        if (Array.isArray(purposes)) trackDynatraceConsentArrayResponseReceived('showConsentFromSettings', purposes);
        resolve(purposes);
      },
      (error) => reject('showConsentFromSettings error: ' + error)
    );
  });
}

function showConsentScreenOnce(): Promise<SEConsentPurpose[] | SEConsentStatus> {
  consentScreenPromise ??= showConsentScreen().finally(() => {
    consentScreenPromise = null;
  });

  return consentScreenPromise;
}

function showConsentFromSettingsOnce(): Promise<SEConsentPurpose[] | SEConsentStatus> {
  consentSettingsPromise ??= showConsentFromSettings().finally(() => {
    consentSettingsPromise = null;
  });

  return consentSettingsPromise;
}
